import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography2 } from '@coursera/cds-core';
import { Box, Container, css } from '@coursera/coursera-ui';

import styles from 'bundles/enterprise-skillset-bank/components/styles';
import { Button, Heading, LargeText, Spacer } from 'bundles/enterprise-ui/components';
import type { SkillSet } from 'bundles/naptimejs/resources/skillSets.v1';

import _t from 'i18n!nls/enterprise-admin-skills';

export const Empty = ({ onBack }: { onBack: () => void }) => (
  <Box flexDirection="column" alignItems="center" justifyContent="center">
    <Spacer size={32} />
    <LargeText as="strong">{_t('You have removed all your selected SkillSets')}</LargeText>
    <Spacer size={24} />
    <LargeText style={{ maxWidth: '531px', textAlign: 'center' }}>
      {_t('Please go back and choose the SkillSets you want to add to your organization')}
    </LargeText>
    <Spacer size={24} />
    <Button onClick={onBack} variant="secondary">
      {_t('Go back and choose SkillSets')}
    </Button>
  </Box>
);

type PropsFromCaller = {
  selectedSkillSets: SkillSet[];
  renderSkillSetCard: (skillset: SkillSet, index: number) => JSX.Element;
  onBack: () => void;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

type Props = PropsFromCaller;

type PropsForFooter = {
  onBack: () => void;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

const SkillSetReviewHeader = (_props: {}) => (
  <Container isFluid rootClassName={styles.HeaderContainer}>
    <div {...css(styles.ContainerContent)}>
      <Spacer size={32} />
      <Heading as="h1">{_t('Review and customize your selections')}</Heading>
      <Spacer size={12} />
      <Typography2 component="p">
        {_t(
          'You can modify the name and description of a SkillSet, any changes will be seen by all programs with this SkillSet.'
        )}
      </Typography2>
      <Spacer size={24} />
    </div>
  </Container>
);

const SkillSetReviewFooter = ({ onBack, onCancel, onConfirm }: PropsForFooter) => (
  <Container isFluid rootClassName={styles.FooterContainer}>
    <Spacer size={24} />
    <Box justifyContent="between" alignItems="center" rootClassName={styles.ContainerContent}>
      <Button variant="secondary" onClick={onBack} data-e2e="skillset-review-back">
        {_t('Back')}
      </Button>
      <Box>
        <Button variant="secondary" onClick={onCancel}>
          {_t('Cancel')}
        </Button>
        <Spacer size={12} />
        <Button variant="primary" onClick={onConfirm} data-e2e="confirm-and-add">
          {_t('Confirm & Add')}
        </Button>
      </Box>
    </Box>
    <Spacer size={24} />
  </Container>
);

const SkillSetReview = ({ selectedSkillSets, renderSkillSetCard, onBack, onCancel, onConfirm }: Props) => {
  return (
    <div data-e2e="skillsets-review">
      <SkillSetReviewHeader />
      <Container isFluid rootClassName={styles.ListContainer}>
        <Spacer size={48} />
        <div {...css(styles.ContainerContent)}>
          {selectedSkillSets.length > 0 && (
            <LargeText as="strong">
              <FormattedMessage
                message={_t('{number} {number, plural, =1 {SkillSet} other {SkillSets}} selected')}
                number={selectedSkillSets.length?.toString()}
              />
            </LargeText>
          )}
          <Spacer size={32} />
          {!selectedSkillSets.length && <Empty onBack={onBack} />}
          {selectedSkillSets.length && (
            <Box flexWrap="wrap" alignItems="stretch" justifyContent="between">
              {selectedSkillSets.map((skillSet, index) => skillSet && renderSkillSetCard(skillSet, index))}
            </Box>
          )}
        </div>
        <Spacer size={32} />
      </Container>
      <SkillSetReviewFooter onBack={onBack} onCancel={onCancel} onConfirm={onConfirm} />
    </div>
  );
};

export default SkillSetReview;
